/**
 * useNotificationRegistration - Auto-register/unregister Firebase push notifications based on auth state
 * Replaces NotificationStore MobX store with a simple hook
 */
import { useCallback, useEffect, useState } from 'react';
import { getToken, onMessage } from 'firebase/messaging';
import { useRootStore } from '../Store/useRootStore';
import { useBackend } from './useBackend';
import { FirebaseMessaging } from '../Store/Firebase/firebase';
import { Config } from '../Config/Config';
import { logger } from '../Utils/logger';

/**
 * Hook to automatically register push notification token when user is authenticated
 * Call this once at the app root level
 */
export function useNotificationRegistration() {
  const { authentication } = useRootStore();
  const backend = useBackend();
  const [token, setToken] = useState<string>('');
  const [hasPermission, setHasPermission] = useState(false);

  // Register notification token with backend
  const registerToken = useCallback(
    async (fcmToken: string): Promise<void> => {
      try {
        logger('NotificationRegistration::registerToken', fcmToken);
        await backend.post('/messaging/notification', {
          token: fcmToken,
          app: 'coach-web',
          type: 'web',
        });
        setToken(fcmToken);
      } catch (err) {
        logger('NotificationRegistration::registerToken error', err);
      }
    },
    [backend],
  );

  // Unregister notification token (optional, rarely used)
  const unregisterToken = useCallback(
    async (fcmToken: string): Promise<void> => {
      try {
        logger('NotificationRegistration::unregisterToken', fcmToken);
        await backend.delete(`/messaging/notification/${fcmToken}`);
        setToken('');
      } catch (err) {
        logger('NotificationRegistration::unregisterToken error', err);
      }
    },
    [backend],
  );

  // Get FCM token from Firebase
  const getNotificationToken = useCallback(async (): Promise<string | void> => {
    if (!FirebaseMessaging) return;
    try {
      const fcmToken = await getToken(FirebaseMessaging, {
        vapidKey: Config.firebase.messaging.publicKey,
      });
      logger('NotificationRegistration::getToken', fcmToken);
      if (fcmToken) {
        await registerToken(fcmToken);
      }
      return fcmToken;
    } catch (err) {
      logger('NotificationRegistration::getToken error', err);
    }
  }, [registerToken]);

  // Request notification permission
  const requestPermission = useCallback(async (): Promise<void> => {
    try {
      const result = await Notification.requestPermission();
      setHasPermission(result === 'granted');
      if (result === 'granted') {
        await getNotificationToken();
      }
    } catch (err) {
      logger('NotificationRegistration::requestPermission error', err);
    }
  }, [getNotificationToken]);

  // Listen to incoming messages
  useEffect(() => {
    if (!FirebaseMessaging) return;
    const unsubscribe = onMessage(FirebaseMessaging, (payload) => {
      logger('NotificationRegistration::received message', payload);
      // Handle foreground notifications here if needed
    });

    return unsubscribe;
  }, []);

  // Auto-register when user logs in, unregister when logs out
  useEffect(() => {
    const athleteId = authentication.athleteId;

    if (athleteId) {
      // User logged in - register for notifications
      // Only register if we don't have a token yet to avoid loops
      if (!token) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        getNotificationToken();
      }
    } else if (token) {
      // User logged out - optionally unregister
      // Note: Usually we don't unregister to allow notifications even when logged out
      // unregisterToken(token);
    }
  }, [authentication.athleteId, token, getNotificationToken]);

  return {
    token,
    hasPermission,
    requestPermission,
  };
}
